import { supabase } from '../lib/supabase'

// Infographics Service - Handles all infographic-related operations
// Transform database fields to frontend camelCase
const transformInfographicData = (infographic) => {
  if (!infographic) return null
  return {
    ...infographic,
    altText: infographic.alt_text,
    imageUrl: infographic.image_url,
    thumbnailUrl: infographic.thumbnail_url,
    fileSize: infographic.file_size,
    fileType: infographic.file_type,
    // Keep original fields for backward compatibility
    alt_text: infographic.alt_text,
    image_url: infographic.image_url,
    thumbnail_url: infographic.thumbnail_url,
    file_size: infographic.file_size,
    file_type: infographic.file_type
  }
}

// Transform array of infographics
const transformInfographicsArray = (infographics) => {
  if (!Array.isArray(infographics)) return []
  return infographics.map(transformInfographicData)
}

export const infographicsService = {
  // Get all published infographics with optional filtering
  async getPublishedInfographics(filters = {}) {
    try {
      let query = supabase
        .from('infographics')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category)
      }
      
      if (filters.difficulty && filters.difficulty !== 'all') {
        query = query.eq('difficulty', filters.difficulty)
      }
      
      if (filters.featured !== undefined) {
        query = query.eq('featured', filters.featured)
      }
      
      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,alt_text.ilike.%${filters.search}%`)
      }
      
      if (filters.tags && filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags)
      }
      
      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      const { data, error } = await query
      
      if (error) throw error
      return transformInfographicsArray(data || [])
    } catch (error) {
      console.error('Error fetching published infographics:', error)
      throw error
    }
  },

  // Get user's own infographics (including drafts)
  async getUserInfographics() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('infographics')
        .select('*')
        .eq('author_id', user.id)
        .order('updated_at', { ascending: false })
      
      if (error) throw error
      return transformInfographicsArray(data || [])
    } catch (error) {
      console.error('Error fetching user infographics:', error)
      throw error
    }
  },

  // Get all infographics regardless of status (for admin/editor use)
  async getAllInfographics(filters = {}) {
    try {
      let query = supabase
        .from('infographics')
        .select('*')
        .order('updated_at', { ascending: false })

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category)
      }
      
      if (filters.difficulty && filters.difficulty !== 'all') {
        query = query.eq('difficulty', filters.difficulty)
      }
      
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status)
      }
      
      if (filters.featured !== undefined) {
        query = query.eq('featured', filters.featured)
      }

      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
      }

      const { data, error } = await query
      if (error) throw error
      
      return transformInfographicsArray(data || [])
    } catch (error) {
      console.error('Error fetching all infographics:', error)
      throw error
    }
  },

  // Get single infographic by ID
  async getInfographicById(id) {
    try {
      const { data, error } = await supabase
        .from('infographics')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) throw error
      return transformInfographicData(data)
    } catch (error) {
      console.error('Error fetching infographic:', error)
      throw error
    }
  },

  // Upload infographic image to storage
  async uploadInfographicImage(file, metadata = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Validate file
      const validationError = validateImageFile(file)
      if (validationError) throw new Error(validationError)

      // Generate unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `infographics/${fileName}`

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('infographic-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('infographic-images')
        .getPublicUrl(filePath)

      // Generate thumbnail if needed
      let thumbnailUrl = null
      try {
        thumbnailUrl = await generateThumbnail(file, filePath)
      } catch (thumbError) {
        console.warn('Could not generate thumbnail:', thumbError)
        thumbnailUrl = publicUrl // Fallback to original image
      }

      return {
        image_url: publicUrl,
        thumbnail_url: thumbnailUrl,
        file_size: file.size,
        file_type: file.type,
        dimensions: await getImageDimensions(file)
      }
    } catch (error) {
      console.error('Error uploading infographic image:', error)
      throw error
    }
  },

  // Create new infographic
  async createInfographic(infographicData, imageFile = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Upload image if provided
      let imageData = {}
      if (imageFile) {
        imageData = await this.uploadInfographicImage(imageFile)
      }

      // Get user profile for author name
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single()

      // Transform camelCase fields to snake_case for database
      const { altText, ...restData } = infographicData
      const infographic = {
        ...restData,
        ...imageData,
        alt_text: altText, // Transform altText to alt_text
        author_id: user.id,
        author_name: profile?.full_name || user.email,
        published_at: infographicData.status === 'published' ? new Date().toISOString() : null
      }

      const { data, error } = await supabase
        .from('infographics')
        .insert([infographic])
        .select()
        .single()
      
      if (error) throw error
      return transformInfographicData(data)
    } catch (error) {
      console.error('Error creating infographic:', error)
      throw error
    }
  },

  // Update infographic
  async updateInfographic(id, updates, newImageFile = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // If new image file provided, upload it
      if (newImageFile) {
        const imageData = await this.uploadInfographicImage(newImageFile)
        Object.assign(updates, imageData)
      }

      // Transform camelCase fields to snake_case for database
      const { altText, ...restUpdates } = updates
      const transformedUpdates = {
        ...restUpdates,
        ...(altText !== undefined && { alt_text: altText }) // Only include if altText is provided
      }

      // Set published_at if status changed to published
      if (transformedUpdates.status === 'published' && !transformedUpdates.published_at) {
        transformedUpdates.published_at = new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('infographics')
        .update(transformedUpdates)
        .eq('id', id)
        .eq('author_id', user.id) // Ensure user can only update their own infographics
        .select()
        .single()
      
      if (error) throw error
      return transformInfographicData(data)
    } catch (error) {
      console.error('Error updating infographic:', error)
      throw error
    }
  },

  // Delete infographic
  async deleteInfographic(id) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Get infographic data to delete associated files
      const { data: infographic } = await supabase
        .from('infographics')
        .select('image_url, thumbnail_url')
        .eq('id', id)
        .eq('author_id', user.id)
        .single()

      if (infographic) {
        // Delete files from storage
        try {
          if (infographic.image_url) {
            const imagePath = extractStoragePath(infographic.image_url)
            if (imagePath) {
              await supabase.storage
                .from('infographic-images')
                .remove([imagePath])
            }
          }
          
          if (infographic.thumbnail_url && infographic.thumbnail_url !== infographic.image_url) {
            const thumbPath = extractStoragePath(infographic.thumbnail_url)
            if (thumbPath) {
              await supabase.storage
                .from('infographic-images')
                .remove([thumbPath])
            }
          }
        } catch (storageError) {
          console.warn('Error deleting storage files:', storageError)
        }
      }

      // Delete database record
      const { error } = await supabase
        .from('infographics')
        .delete()
        .eq('id', id)
        .eq('author_id', user.id)
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting infographic:', error)
      throw error
    }
  },

  // Record infographic view for analytics
  async recordView(infographicId, userAgent = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const viewData = {
        infographic_id: infographicId,
        user_id: user?.id || null,
        user_agent: userAgent || navigator.userAgent
      }

      // Insert view record
      const { error: viewError } = await supabase
        .from('infographic_views')
        .insert([viewData])
      
      if (viewError) console.warn('Error recording view:', viewError)

      // Increment view count using the database function
      const { error: countError } = await supabase
        .rpc('increment_infographic_views', { infographic_uuid: infographicId })
      
      if (countError) console.warn('Error incrementing view count:', countError)
      
      return true
    } catch (error) {
      console.warn('Error recording infographic view:', error)
      return false
    }
  },

  // Record download for analytics
  async recordDownload(infographicId) {
    try {
      const { error } = await supabase
        .from('infographics')
        .update({ downloads: supabase.raw('downloads + 1') })
        .eq('id', infographicId)
      
      if (error) console.warn('Error recording download:', error)
      return true
    } catch (error) {
      console.warn('Error recording infographic download:', error)
      return false
    }
  },

  // Like/unlike infographic
  async toggleLike(infographicId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Check if user already liked this infographic
      const { data: existingLike } = await supabase
        .from('infographic_likes')
        .select('id')
        .eq('infographic_id', infographicId)
        .eq('user_id', user.id)
        .single()

      if (existingLike) {
        // Unlike - remove like
        const { error } = await supabase
          .from('infographic_likes')
          .delete()
          .eq('infographic_id', infographicId)
          .eq('user_id', user.id)
        
        if (error) throw error

        // Decrement like count
        const { data: currentData } = await supabase
          .from('infographics')
          .select('likes')
          .eq('id', infographicId)
          .single()
        
        const newLikes = Math.max(0, (currentData?.likes || 0) - 1)
        await supabase
          .from('infographics')
          .update({ likes: newLikes })
          .eq('id', infographicId)
        
        return { liked: false }
      } else {
        // Like - add like
        const { error } = await supabase
          .from('infographic_likes')
          .insert([{ infographic_id: infographicId, user_id: user.id }])
        
        if (error) throw error

        // Increment like count
        const { data: currentData } = await supabase
          .from('infographics')
          .select('likes')
          .eq('id', infographicId)
          .single()
        
        const newLikes = (currentData?.likes || 0) + 1
        await supabase
          .from('infographics')
          .update({ likes: newLikes })
          .eq('id', infographicId)
        
        return { liked: true }
      }
    } catch (error) {
      console.error('Error toggling infographic like:', error)
      throw error
    }
  },

  // Check if user liked an infographic
  async checkUserLike(infographicId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false

      const { data } = await supabase
        .from('infographic_likes')
        .select('id')
        .eq('infographic_id', infographicId)
        .eq('user_id', user.id)
        .single()
      
      return !!data
    } catch (error) {
      return false
    }
  },

  // Get user likes for multiple infographics (batch operation)
  async getUserLikes(infographicIds) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || !infographicIds || infographicIds.length === 0) return []

      const { data, error } = await supabase
        .from('infographic_likes')
        .select('infographic_id')
        .eq('user_id', user.id)
        .in('infographic_id', infographicIds)
      
      if (error) throw error
      return data ? data.map(like => like.infographic_id) : []
    } catch (error) {
      console.error('Error fetching user likes:', error)
      return []
    }
  },

  // Get featured infographics
  async getFeaturedInfographics(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('infographics')
        .select('*')
        .eq('status', 'published')
        .eq('featured', true)
        .order('published_at', { ascending: false })
        .limit(limit)
      
      if (error) throw error
      return transformInfographicsArray(data || [])
    } catch (error) {
      console.error('Error fetching featured infographics:', error)
      throw error
    }
  },

  // Get popular infographics (by views)
  async getPopularInfographics(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('infographics')
        .select('*')
        .eq('status', 'published')
        .order('views', { ascending: false })
        .limit(limit)
      
      if (error) throw error
      return transformInfographicsArray(data || [])
    } catch (error) {
      console.error('Error fetching popular infographics:', error)
      throw error
    }
  },

  // Search infographics with advanced options
  async searchInfographics(searchTerm, options = {}) {
    try {
      let query = supabase
        .from('infographics')
        .select('*')
        .eq('status', 'published')

      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,alt_text.ilike.%${searchTerm}%`)
      }

      if (options.category) {
        query = query.eq('category', options.category)
      }

      if (options.difficulty) {
        query = query.eq('difficulty', options.difficulty)
      }

      if (options.tags && options.tags.length > 0) {
        query = query.overlaps('tags', options.tags)
      }

      // Sorting
      const sortBy = options.sortBy || 'published_at'
      const sortOrder = options.sortOrder || 'desc'
      query = query.order(sortBy, { ascending: sortOrder === 'asc' })

      if (options.limit) {
        query = query.limit(options.limit)
      }

      const { data, error } = await query
      
      if (error) throw error
      return transformInfographicsArray(data || [])
    } catch (error) {
      console.error('Error searching infographics:', error)
      throw error
    }
  },

  // Get infographic analytics
  async getInfographicAnalytics(infographicId) {
    try {
      // Get basic infographic info
      const { data: infographic } = await supabase
        .from('infographics')
        .select('views, likes, downloads, created_at, published_at')
        .eq('id', infographicId)
        .single()

      // Get view history (last 30 days)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const { data: viewHistory } = await supabase
        .from('infographic_views')
        .select('viewed_at')
        .eq('infographic_id', infographicId)
        .gte('viewed_at', thirtyDaysAgo.toISOString())
        .order('viewed_at', { ascending: true })

      // Get like history
      const { data: likeHistory } = await supabase
        .from('infographic_likes')
        .select('created_at')
        .eq('infographic_id', infographicId)
        .order('created_at', { ascending: true })

      return {
        ...infographic,
        viewHistory: viewHistory || [],
        likeHistory: likeHistory || []
      }
    } catch (error) {
      console.error('Error fetching infographic analytics:', error)
      throw error
    }
  },

  // Multiple Image Management Methods
  
  // Upload multiple images for infographic
  async uploadInfographicImages(infographicId, files, options = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const uploadPromises = files.map(async (file, index) => {
        // Validate each file
        const validationError = validateImageFile(file)
        if (validationError) throw new Error(`File ${index + 1}: ${validationError}`)

        // Generate unique filename
        const fileExt = file.name.split('.').pop()
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
        const filePath = `infographics/${infographicId}/images/${fileName}`

        // Upload file to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('infographic-images')
          .upload(filePath, file)

        if (uploadError) throw uploadError

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('infographic-images')
          .getPublicUrl(filePath)

        // Get image dimensions
        const dimensions = await getImageDimensions(file)

        // Save image record to database
        const imageData = {
          infographic_id: infographicId,
          image_url: publicUrl,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          width: dimensions.width,
          height: dimensions.height,
          alt_text: options.altTexts?.[index] || '',
          caption: options.captions?.[index] || '',
          display_order: options.displayOrders?.[index] || index,
          is_primary: options.primaryIndex === index,
          uploaded_by: user.id
        }

        const { data, error } = await supabase
          .from('infographic_images')
          .insert([imageData])
          .select()
          .single()

        if (error) throw error
        return data
      })

      const results = await Promise.all(uploadPromises)
      return results
    } catch (error) {
      console.error('Error uploading infographic images:', error)
      throw error
    }
  },

  // Get infographic images
  async getInfographicImages(infographicId) {
    try {
      const { data, error } = await supabase
        .from('infographic_images')
        .select('*')
        .eq('infographic_id', infographicId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching infographic images:', error)
      throw error
    }
  },

  // Get infographic with images
  async getInfographicWithImages(infographicId) {
    try {
      const { data, error } = await supabase
        .rpc('get_infographic_with_images', { infographic_uuid: infographicId })

      if (error) throw error
      
      if (data && data.length > 0) {
        return {
          ...transformInfographicData(data[0].infographic_data),
          images: data[0].images_data
        }
      }
      
      return null
    } catch (error) {
      console.error('Error fetching infographic with images:', error)
      throw error
    }
  },

  // Update infographic image
  async updateInfographicImage(imageId, updates) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('infographic_images')
        .update(updates)
        .eq('id', imageId)
        .eq('uploaded_by', user.id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating infographic image:', error)
      throw error
    }
  },

  // Delete infographic image
  async deleteInfographicImage(imageId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Get image info first
      const { data: image } = await supabase
        .from('infographic_images')
        .select('image_url, uploaded_by')
        .eq('id', imageId)
        .single()

      if (!image) throw new Error('Image not found')
      if (image.uploaded_by !== user.id) throw new Error('Unauthorized')

      // Extract file path from URL
      const url = new URL(image.image_url)
      const filePath = url.pathname.split('/storage/v1/object/public/infographic-images/')[1]

      // Delete file from storage
      const { error: storageError } = await supabase.storage
        .from('infographic-images')
        .remove([filePath])

      if (storageError) console.warn('Error deleting file from storage:', storageError)

      // Delete image record
      const { error } = await supabase
        .from('infographic_images')
        .delete()
        .eq('id', imageId)
        .eq('uploaded_by', user.id)

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting infographic image:', error)
      throw error
    }
  },

  // Set primary image
  async setPrimaryInfographicImage(imageId) {
    try {
      const { error } = await supabase
        .rpc('set_primary_infographic_image', { image_uuid: imageId })

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error setting primary image:', error)
      throw error
    }
  },

  // Record image download
  async recordImageDownload(imageId) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const downloadData = {
        image_id: imageId,
        user_id: user?.id || null,
        user_agent: navigator.userAgent
      }

      const { error } = await supabase
        .from('infographic_downloads')
        .insert([downloadData])

      if (error) console.warn('Error recording download:', error)
      return true
    } catch (error) {
      console.warn('Error recording image download:', error)
      return false
    }
  },

  // Get image download stats
  async getImageDownloadStats(imageId) {
    try {
      const { data, error } = await supabase
        .from('infographic_downloads')
        .select('downloaded_at, user_id')
        .eq('image_id', imageId)
        .order('downloaded_at', { ascending: false })

      if (error) throw error
      
      const totalDownloads = data?.length || 0
      const uniqueUsers = new Set(data?.filter(d => d.user_id).map(d => d.user_id)).size
      
      return {
        totalDownloads,
        uniqueUsers,
        downloadHistory: data || []
      }
    } catch (error) {
      console.error('Error fetching download stats:', error)
      throw error
    }
  },

  // Reorder images
  async reorderInfographicImages(infographicId, imageOrders) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const updatePromises = imageOrders.map(({ imageId, displayOrder }) => 
        supabase
          .from('infographic_images')
          .update({ display_order: displayOrder })
          .eq('id', imageId)
          .eq('uploaded_by', user.id)
      )

      await Promise.all(updatePromises)
      return true
    } catch (error) {
      console.error('Error reordering images:', error)
      throw error
    }
  }
}

// Utility functions
export const validateImageFile = (file) => {
  if (!file) return 'No file provided'
  
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  if (!allowedTypes.includes(file.type)) {
    return 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.'
  }
  
  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024 // 10MB in bytes
  if (file.size > maxSize) {
    return 'File size too large. Please upload an image smaller than 10MB.'
  }
  
  return null
}

export const getImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      })
    }
    
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve({ width: 0, height: 0 })
    }
    
    img.src = url
  })
}

export const generateThumbnail = async (file, originalPath) => {
  // For now, return the original image URL as thumbnail
  // In a production environment, you might want to implement actual thumbnail generation
  const { data: { publicUrl } } = supabase.storage
    .from('infographic-images')
    .getPublicUrl(originalPath)
  
  return publicUrl
}

export const extractStoragePath = (url) => {
  if (!url) return null
  
  try {
    // Extract path from Supabase storage URL
    const urlObj = new URL(url)
    const pathParts = urlObj.pathname.split('/')
    const bucketIndex = pathParts.findIndex(part => part === 'infographic-images')
    
    if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
      return pathParts.slice(bucketIndex + 1).join('/')
    }
    
    return null
  } catch (error) {
    console.warn('Error extracting storage path:', error)
    return null
  }
}

export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const getInfographicUrl = (infographic) => {
  if (!infographic) return '#'
  const slug = infographic.title.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `/infographics/${infographic.id}/${slug}`
}

export const formatInfographicDate = (dateString) => {
  if (!dateString) return 'Unknown date'
  
  const date = new Date(dateString)
  const now = new Date()
  
  // Check if it's today
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  
  const diffTime = today.getTime() - dateOnly.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    // Same day - show time
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    return `Today at ${timeStr}`
  }
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`
  return `${Math.ceil(diffDays / 365)} years ago`
}

export const validateInfographic = (infographic) => {
  const errors = []
  
  if (!infographic.title || infographic.title.trim().length < 3) {
    errors.push('Title must be at least 3 characters long')
  }
  
  if (!infographic.category) {
    errors.push('Category is required')
  }
  
  if (!infographic.difficulty) {
    errors.push('Difficulty level is required')
  }
  
  if (infographic.description && infographic.description.length > 500) {
    errors.push('Description must be less than 500 characters')
  }
  
  if (infographic.alt_text && infographic.alt_text.length > 200) {
    errors.push('Alt text must be less than 200 characters')
  }
  
  return errors
}

export default infographicsService